import { TABLE_PERMISSION_SCOPES, PERMISSION_DEPENDENCIES } from "./TablePermissionScopes";
import _ from "lodash";

/**
 * Tablo yetki kontrolü yapar
 * @param {Object} table - Tablo nesnesi
 * @param {String} scope - Kontrol edilecek yetki scope'u
 * @param {Object} user - Kullanıcı nesnesi
 * @param {Object} adminUser - Admin kullanıcı nesnesi
 * @param {Array} userDepartments - Kullanıcının dahil olduğu departmanlar
 * @returns {Boolean} - Yetkili ise true, değilse false
 */
export function checkTablePermission(table, scope, user, adminUser, userDepartments = []) {
    // 1. AdminUser varsa full yetki
    if (adminUser) {
        return true;
    }

    // 3. Tablo yoksa erişim yok
    if (!table) {
        return false;
    }

    // 4. permissionConfig yoksa veya mode public ise herkes yetkili
    if (!table.permissionConfig || table.permissionConfig.mode === "public") {
        return true;
    }

    // 5. Custom modda, kullanıcı ve departman yetkilerini kontrol et
    if (table.permissionConfig.mode === "custom") {
        const userScopes = getUserTableScopes(table, user, adminUser, userDepartments);

        // Scope'un bağımlılıklarını da kontrol et
        const requiredScopes = [scope, ...getScopeDependencies(scope)];

        // Tüm gerekli scope'lar mevcut mu kontrol et
        return requiredScopes.every((requiredScope) => userScopes.includes(requiredScope));
    }

    // 2. Root kullanıcı ise full yetki
    if (user && user.role && user.role.isRoot) {
        return true;
    }

    return false;
}

/**
 * Kullanıcının tablodaki tüm yetkilerini döndürür
 * @param {Object} table - Tablo nesnesi
 * @param {Object} user - Kullanıcı nesnesi
 * @param {Object} adminUser - Admin kullanıcı nesnesi
 * @param {Array} userDepartments - Kullanıcının dahil olduğu departmanlar
 * @returns {Array} - Kullanıcının sahip olduğu scope'lar
 */
export function getUserTableScopes(table, user, adminUser, userDepartments = []) {
    // AdminUser veya root kullanıcı ise tüm yetkiler
    if (adminUser) {
        return Object.values(TABLE_PERMISSION_SCOPES);
    }

    // Tablo yoksa veya public modda ise tüm yetkiler
    if (!table || !table.permissionConfig || table.permissionConfig.mode === "public") {
        return Object.values(TABLE_PERMISSION_SCOPES);
    }

    // Custom modda yetkiler
    if (table.permissionConfig.mode === "custom" && table.permissionConfig.custom) {
        const config = table.permissionConfig.custom;
        let scopes = [];

        // Kullanıcıya özel yetki var mı kontrol et
        const userConfig = config.find(
            (c) => c.target === "user" && c.user && user && c.user.toString() === user._id.toString()
        );

        if (userConfig) {
            // Kullanıcıya özel yetki varsa SADECE onu kullan (departman yetkisi IGNORE edilir)
            scopes = userConfig.scopes || [];
        } else {
            // Kullanıcıya özel yetki yoksa departman yetkilerini merge et
            const userSubjects = user.subjects || [];

            // Eğer subjects boşsa, TÜM departmanların yetkilerine sahiptir
            if (!userSubjects || userSubjects.length === 0) {
                config.forEach((c) => {
                    if (c.target === "department" && c.scopes) {
                        scopes = _.union(scopes, c.scopes);
                    }
                });
            } else {
                // Sadece kullanıcının dahil olduğu departmanların yetkilerini merge et
                const userDepartmentIds = userSubjects.map((d) => {
                    return typeof d === "object" ? d._id.toString() : d.toString();
                });

                config.forEach((c) => {
                    if (c.target === "department" && c.department) {
                        const deptId = c.department.toString();
                        if (userDepartmentIds.includes(deptId)) {
                            scopes = _.union(scopes, c.scopes || []);
                        }
                    }
                });
            }
        }

        return scopes;
    }
    // Root kullanıcı ise tüm yetkiler
    if (user && user.role && user.role.isRoot) {
        return Object.values(TABLE_PERMISSION_SCOPES);
    }
    return [];
}

/**
 * Bir scope'un bağımlılıklarını döndürür
 * @param {String} scope - Scope
 * @returns {Array} - Bağımlılık scope'ları
 */
export function getScopeDependencies(scope) {
    return PERMISSION_DEPENDENCIES[scope] || [];
}

/**
 * Verilen scope listesinin bağımlılıklarını doğrular
 * @param {Array} scopes - Kontrol edilecek scope'lar
 * @returns {Object} - { valid: boolean, missing: Array }
 */
export function validatePermissionDependencies(scopes) {
    const missing = [];

    scopes.forEach((scope) => {
        const dependencies = getScopeDependencies(scope);
        dependencies.forEach((dep) => {
            if (!scopes.includes(dep)) {
                missing.push({
                    scope,
                    missingDependency: dep,
                });
            }
        });
    });

    return {
        valid: missing.length === 0,
        missing,
    };
}

/**
 * Scope eklerken otomatik olarak bağımlılıkları da ekler
 * @param {Array} currentScopes - Mevcut scope'lar
 * @param {String} newScope - Eklenecek scope
 * @returns {Array} - Bağımlılıkları dahil tüm scope'lar
 */
export function addScopeWithDependencies(currentScopes, newScope) {
    const dependencies = getScopeDependencies(newScope);
    return _.union(currentScopes, [newScope, ...dependencies]);
}

/**
 * Scope kaldırırken bağımlı olan diğer scope'ları da kaldırır
 * @param {Array} currentScopes - Mevcut scope'lar
 * @param {String} scopeToRemove - Kaldırılacak scope
 * @returns {Array} - Kalan scope'lar
 */
export function removeScopeWithDependents(currentScopes, scopeToRemove) {
    // Önce kendisini kaldır
    let remaining = currentScopes.filter((s) => s !== scopeToRemove);

    // Kaldırılan scope'a bağımlı olan scope'ları bul ve onları da kaldır
    const dependentScopes = Object.keys(PERMISSION_DEPENDENCIES).filter((scope) => {
        const deps = PERMISSION_DEPENDENCIES[scope];
        return deps.includes(scopeToRemove);
    });

    dependentScopes.forEach((depScope) => {
        remaining = remaining.filter((s) => s !== depScope);
    });

    return remaining;
}

/**
 * Kullanıcının tabloya erişimi var mı kontrol eder (en temel kontrol)
 * @param {Object} table - Tablo nesnesi
 * @param {Object} user - Kullanıcı nesnesi
 * @param {Object} adminUser - Admin kullanıcı nesnesi
 * @param {Array} userDepartments - Kullanıcının dahil olduğu departmanlar
 * @returns {Boolean} - Erişimi varsa true
 */
export function canAccessTable(table, user, adminUser, userDepartments = []) {
    return checkTablePermission(table, TABLE_PERMISSION_SCOPES.TABLE_READ, user, adminUser, userDepartments);
}

/**
 * Belirli bir kullanıcı/departman için scope toggle yapar
 * @param {Array} currentScopes - Mevcut scope'lar
 * @param {String} scope - Toggle edilecek scope
 * @returns {Array} - Yeni scope listesi
 */
export function toggleScope(currentScopes, scope) {
    if (currentScopes.includes(scope)) {
        // Scope mevcutsa, bağımlıları ile birlikte kaldır
        return removeScopeWithDependents(currentScopes, scope);
    } else {
        // Scope yoksa, bağımlılıkları ile birlikte ekle
        return addScopeWithDependencies(currentScopes, scope);
    }
}

/**
 * Kullanıcının departmanlarını getirir
 * @param {Object} user - Kullanıcı nesnesi
 * @returns {Array} - Departman listesi
 */
export function getUserDepartments(user) {
    if (!user || !user.subjects) {
        return [];
    }

    // subjects array ise direkt dön, değilse boş array
    return Array.isArray(user.subjects) ? user.subjects : [];
}

/**
 * Kullanıcının tablo gizlilik ayarlarını düzenleme yetkisi var mı kontrol eder
 * @param {Object} table - Tablo nesnesi
 * @param {Object} user - Kullanıcı nesnesi
 * @param {Object} adminUser - Admin kullanıcı nesnesi
 * @returns {Boolean} - Yetkisi varsa true
 */
export function canEditTablePermissions(table, user, adminUser) {
    console.log("canEditTablePermissions", { table, user, adminUser });

    // AdminUser ise izin ver
    if (adminUser) return true;
    
    if (!table || !user) return false;


    // isOwner kontrolü
    if (user.isOwner) return true;

    // createdBy kontrolü
    if (table.createdBy && user._id.toString() === table.createdBy.toString()) {
        return true;
    }

    // Root kullanıcı kontrolü - full yetkili mi?
    if (user.role && user.role.isRoot) {
        // Tablo custom modda mı ve kullanıcıya özel yetki var mı?
        if (table.permissionConfig && table.permissionConfig.mode === "custom" && table.permissionConfig.custom) {
            const userConfig = table.permissionConfig.custom.find(
                (t) => t.target === "user" && t.user && t.user.toString() === user._id.toString()
            );

            if (userConfig) {
                // Kullanıcıya özel yetki varsa, FULL yetkisi var mı kontrol et
                const { TABLE_PERMISSION_SCOPES } = require("./TablePermissionScopes");
                const allScopes = Object.values(TABLE_PERMISSION_SCOPES);
                const hasAllScopes = allScopes.every((scope) => (userConfig.scopes || []).includes(scope));
                return hasAllScopes;
            }
        }

        // Public modda veya kullanıcıya özel yetki yoksa root kullanıcıya izin ver
        return true;
    }

    return false;
}
